import { z } from "zod";
import { Competency, CompetencySchema } from "./schemas";
import { decisionMaking } from "./competencies/decision-making";
import { teamwork } from "./competencies/teamwork";
import { integrity } from "./competencies/integrity";
import { interactiveCommunication } from "./competencies/interactive-communication";
import { problemSolving } from "./competencies/problem-solving";
import { conflictManagement } from "./competencies/conflict-management";
import { assertivenessSelfConfidence } from "./competencies/assertiveness-self-confidence";
import { adaptability } from "./competencies/adaptability";
import { organizationalAwareness } from "./competencies/organizational-awareness";
import { riskManagement } from "./competencies/risk-management";
import { stressManagement } from "./competencies/stress-management";
import { compassion } from "./competencies/compassion";
import { leadership } from "./competencies/leadership";
import { excellence } from "./competencies/excellence";
import { criticalStrategicThinking } from "./competencies/critical-strategic-thinking";
import { planningOrganization } from "./competencies/planning-organization";
import { valuingDiversity } from "./competencies/valuing-diversity";
import { interpersonalSkills } from "./competencies/interpersonal-skills";
import { resiliency } from "./competencies/resiliency";
import { motivationResultsOriented } from "./competencies/motivation-results-oriented";
import { continuousLearningImprovement } from "./competencies/continuous-learning-improvement";
import { analyticalThinking } from "./competencies/analytical-thinking";
import { dealingWithDifficultSituations } from "./competencies/dealing-with-difficult-situations";
import { communityServiceOrientation } from "./competencies/community-service-orientation";
import { selfInitiativePerseverance } from "./competencies/self-initiative-perseverance";
import { decisiveness } from "./competencies/decisiveness";
import { selfAwareness } from "./competencies/self-awareness";
import { acceptanceOfResponsibility } from "./competencies/acceptance-of-responsibility";
import { timeManagement } from "./competencies/time-management";
import { workEthic } from "./competencies/work-ethic";
import { selfControlComposure } from "./competencies/self-control-composure";
import { attentionToDetail } from "./competencies/attention-to-detail";
import { professionalism } from "./competencies/professionalism";
import { valuesEthics } from "./competencies/values-ethics";
import { judgement } from "./competencies/judgement";
import { courageOfConvictions } from "./competencies/courage-of-convictions";
import { relationshipBuilding } from "./competencies/relationship-building";
import { oralCommunication } from "./competencies/oral-communication";
import { inclusivity } from "./competencies/inclusivity";
import { stressTolerance } from "./competencies/stress-tolerance";
import { communityInvolvementAwareness } from "./competencies/community-involvement-awareness";
import { developingOthers } from "./competencies/developing-others";
import { strategicThinking } from "./competencies/strategic-thinking";
import { behaviouralFlexibility } from "./competencies/behavioural-flexibility";
import { publicAccountability } from "./competencies/public-accountability";
import { communityRelationsMediaManagement } from "./competencies/community-relations-media-management";
import { hrTalentManagement } from "./competencies/hr-talent-management";
import { changeManagement } from "./competencies/change-management";
import { financialManagement } from "./competencies/financial-management";
import { factFindingObservationalSkills } from "./competencies/fact-finding-observational-skills";

const competencies: Competency[] = [
  decisionMaking,
  teamwork,
  integrity,
  interactiveCommunication,
  problemSolving,
  conflictManagement,
  assertivenessSelfConfidence,
  adaptability,
  organizationalAwareness,
  riskManagement,
  stressManagement,
  compassion,
  leadership,
  excellence,
  criticalStrategicThinking,
  planningOrganization,
  valuingDiversity,
  interpersonalSkills,
  resiliency,
  motivationResultsOriented,
  continuousLearningImprovement,
  analyticalThinking,
  dealingWithDifficultSituations,
  communityServiceOrientation,
  selfInitiativePerseverance,
  decisiveness,
  selfAwareness,
  acceptanceOfResponsibility,
  timeManagement,
  workEthic,
  selfControlComposure,
  attentionToDetail,
  professionalism,
  valuesEthics,
  judgement,
  courageOfConvictions,
  relationshipBuilding,
  oralCommunication,
  inclusivity,
  stressTolerance,
  communityInvolvementAwareness,
  developingOthers,
  strategicThinking,
  behaviouralFlexibility,
  publicAccountability,
  communityRelationsMediaManagement,
  hrTalentManagement,
  changeManagement,
  financialManagement,
  factFindingObservationalSkills,
];

export const COMPETENCIES: Competency[] = z
  .array(CompetencySchema)
  .parse(competencies);

// Keyed by `string` rather than `CompetencySlug` because lookups happen with
// competency ids read from the database, which are arbitrary strings — the
// `| undefined` value type forces callers to handle a miss.
export const COMPETENCY_BY_ID: Record<string, Competency | undefined> =
  Object.fromEntries(
    COMPETENCIES.map((competency) => [competency.id, competency])
  );
